import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

const AudioEngine = () => {
  const audioRef = useRef(null);
  const dispatch = useDispatch();

  // 📡 Récupération de l'état Redux unifié
  const { isPlaying, currentTrack, isMute, isLoop, isPlaylistLoop, volume, seekTime } = useSelector((state) => state.player);

  // 🎵 1. GESTION DU CHARGEMENT DE LA SOURCE AUDIO
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Si on clique sur STOP ou aucune piste active
    if (!currentTrack || !currentTrack.url) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }

    // On change de source uniquement si l'URL est différente
    if (audio.src !== currentTrack.url) {
      audio.src = currentTrack.url;
      audio.load(); // Recharge proprement le décodeur du navigateur
    }
  }, [currentTrack]); // Déclenché uniquement si la piste change

  // 🎛️ 2. GESTION DES COMMANDES (Play, Pause, Volume, Muet)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    // Gestion fluide des décibels
    audio.volume = isMute ? 0 : volume / 100;
    audio.muted = isMute;

    // ⚡ Déclencheur mécanique de lecture
    if (isPlaying) {
      // On s'assure que le navigateur a le droit de lire (sécurité Autoplay)
      audio.play().catch((err) => console.log("Attente d'action utilisateur :", err));
    } else {
      audio.pause();
    }
  }, [isPlaying, isMute, volume, currentTrack]);

  // 📅 3. BARRE DE DÉFILEMENT MANUELLE (Seek)
  useEffect(() => {
    if (audioRef.current && seekTime !== null) {
      audioRef.current.currentTime = seekTime;
    }
  }, [seekTime]);

  // 📡 4. ÉCOUTEURS D'ÉVÉNEMENTS PHYSIQUES
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Transmet les secondes qui défilent à Redux
    const handleTimeUpdate = () => {
      dispatch({ type: 'UPDATE_TIME', payload: audio.currentTime });
    };

    // 🎯 TRANSMISSION DE LA DURÉE : Déclenché dès que le fichier est chargé en mémoire !
    const handleDurationUpdate = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        dispatch({ type: 'SET_DURATION', payload: audio.duration });
      }
    };

    const handleEnded = () => {
      if (isLoop) {
        audio.currentTime = 0; // Remet la tête de lecture au début
        audio.play().catch((err) => console.log("Erreur replay boucle :", err));
      } else if (isPlaylistLoop) {
          dispatch({ type: 'NEXT_TRACK' }); 
      } else {
        dispatch({ type: 'STOP_TRACK' });
      }
    };

    // Attachement des écouteurs officiels HTML5
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationUpdate);
    audio.addEventListener('canplay', handleDurationUpdate); // Doublon de sécurité pour les fichiers locaux !
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationUpdate);
      audio.removeEventListener('canplay', handleDurationUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, dispatch, isLoop, isPlaylistLoop]);

  return <audio ref={audioRef} preload="auto" />;
};

export default AudioEngine;
